import { Injectable } from '@angular/core';
import { NativeDateAdapter, type MatDateFormats } from '@angular/material/core';

/** Marker passed as `display.dateInput` so the input shows date and time together. */
export const QWD_DATETIME_INPUT_FORMAT = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
};

export const QWD_DATETIME_FORMATS: MatDateFormats = {
  parse: {
    dateInput: QWD_DATETIME_INPUT_FORMAT,
  },
  display: {
    dateInput: QWD_DATETIME_INPUT_FORMAT,
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};

const DATETIME_INPUT_PATTERN =
  /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})(?:\s*(AM|PM))?$/i;

/**
 * Formats and parses `app-datetime-field` values as `DD/MM/YYYY HH:mm:ss`.
 * Provided from the app root because the picker dialog is created by root `MatDialog`.
 */
@Injectable()
export class QwdDatetimeAdapter extends NativeDateAdapter {
  /**
   * The picker reads this string with `new Date(...)` and then sets the clock time.
   * A UTC date-only value moves midnight to the previous local day, so 12:00:00 AM
   * on the end field was stored as 11:59:59 PM the day before.
   */
  override toIso8601(date: Date): string {
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  override format(date: Date, displayFormat: Object): string {
    if (displayFormat === QWD_DATETIME_INPUT_FORMAT) {
      return formatQwdDateTime(date);
    }
    return super.format(date, displayFormat);
  }

  override parse(value: any): Date | null {
    if (typeof value !== 'string') {
      return value instanceof Date ? value : null;
    }
    const text = value.trim();
    if (!text) {
      return null;
    }
    const match = text.match(DATETIME_INPUT_PATTERN);
    if (!match) {
      return this.invalid();
    }

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    let hour = Number(match[4]);
    const minute = Number(match[5]);
    const second = Number(match[6]);
    const ampm = match[7]?.toUpperCase();

    if (month < 1 || month > 12 || day < 1 || day > 31 || minute > 59 || second > 59) {
      return this.invalid();
    }
    if (ampm) {
      if (hour < 1 || hour > 12) {
        return this.invalid();
      }
      if (ampm === 'AM') {
        hour = hour === 12 ? 0 : hour;
      } else if (hour !== 12) {
        hour += 12;
      }
    } else if (hour > 23) {
      return this.invalid();
    }

    const date = new Date(year, month - 1, day, hour, minute, second, 0);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return this.invalid();
    }
    return date;
  }

  override isValid(date: Date): boolean {
    return date instanceof Date && super.isValid(date);
  }
}

export function formatQwdDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

export function formatQwdDateTime(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  const second = String(date.getSeconds()).padStart(2, '0');
  return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
}
